/**
 * Shared responsive breakpoints for the portal.
 *
 * `md` and `lg` stay pinned to their historical values in both themes - moving
 * them would change every existing `md`/`lg` condition in the page code at
 * once. Note that they mean different widths in the two themes (1200 in the
 * shell, 1400 in the content theme), which is exactly why new work should not
 * use them.
 *
 * What was missing is everything *below* 1200px. With `sm`, `md` and `lg` all
 * collapsed onto the same value there was only one tier under desktop, so a
 * 390px phone and a 1100px laptop were handed the identical layout. These
 * values add the missing tiers:
 *
 *   xs      0     small phones
 *   phone   480   phones
 *   sm      600   above this, no longer a phone in portrait
 *   tablet  900   tablets in landscape and small laptops
 *   md/lg         the existing desktop layout, unchanged
 *
 * Use the semantic `phone` and `tablet` keys for new responsive work; they mean
 * the same width in both themes, whereas `md` and `lg` do not.
 */

/**
 * Widths (px) at which the layout meaningfully changes.
 *
 * PHONE_BREAKPOINT is the important one: it is where the portal stops
 * attempting a desktop layout. It sits at 600 rather than somewhere in the
 * 700s so that no tablet crosses it in either orientation - an iPad in
 * portrait is 768px wide and the desktop layout is fine there.
 */
export const NARROW_PHONE_BREAKPOINT = 480
export const PHONE_BREAKPOINT = 600
export const TABLET_BREAKPOINT = 900

/**
 * Breakpoints for the application shell (`AppProvider`).
 *
 * Note that these do NOT govern Toolpad's `DashboardLayout`, even though it is
 * the shell: `DashboardLayout` is rendered inside the nested `ThemeProvider`
 * that supplies `contentTheme`, and MUI's nested provider shallow-merges
 * (`{ ...outer, ...local }`), so `contentTheme.breakpoints` replaces this whole
 * object for everything below it. See `SHELL_NAV_BREAKPOINT`.
 */
export const SHELL_BREAKPOINTS = {
  xs: 0,
  phone: NARROW_PHONE_BREAKPOINT,
  sm: PHONE_BREAKPOINT,
  tablet: TABLET_BREAKPOINT,
  md: 1200,
  lg: 1200,
  xl: 1536
}

/**
 * Breakpoints for page content, and - because of the nesting described above -
 * for Toolpad's `DashboardLayout` as well.
 *
 * `md`/`lg` stay at 1400 so the existing `{ xs, md }` conditions throughout the
 * pages keep switching exactly where they do today. `dateBar` is kept because
 * the home date-range selector already used it.
 */
export const CONTENT_BREAKPOINTS = {
  xs: 0,
  phone: NARROW_PHONE_BREAKPOINT,
  sm: PHONE_BREAKPOINT,
  dateBar: 600,
  tablet: TABLET_BREAKPOINT,
  md: 1400,
  lg: 1400,
  xl: 1536
}

/**
 * The width at which the shell swaps its permanent sidebar for the hamburger
 * drawer.
 *
 * `DashboardLayout` decides this with `theme.breakpoints.down('md')` against
 * whichever theme is nearest in the tree - which is `contentTheme`, not
 * `shellTheme` - so the real threshold is CONTENT_BREAKPOINTS.md, 1400px, and
 * has been since before this file existed. Anything that needs to agree with
 * the navigation (such as whether the app bar still has room for the inline
 * selection controls) must use this value rather than assuming 1200.
 */
export const SHELL_NAV_BREAKPOINT = CONTENT_BREAKPOINTS.md
